import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Monitor } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { LoadingBlock } from "../components/Spinner";
import { SheetCard, StatusTag, type StatusTone } from "../components/Drawing";
import { EmptyState, Field, ModalHeader, ModalOverlay, dangerBtn, fmtDateTime, input, secondaryBtn, tableHeadRow, textarea } from "./resilience/shared";
import type { DeviceRow } from "./DevicesPage";

// One device: its latest Cyber Essentials findings by theme, with how each was established, the
// helper's audit trail and the instructions sent to it (backend routes/devices.routes.ts).

interface Finding {
  Id: string; Category?: string | null; Title: string; Status: "Pass" | "Fail" | "Unknown"; Severity?: string | null;
  Detail?: string | null; Why?: string | null; Fix?: string | null; Method?: "Enforced" | "Verified" | "Guided" | "Declared" | null;
  Tags?: { CyberEssentials?: string | null; ISO27001?: string | null } | null;
}
interface Command {
  id: string; op: string; path: string | null; programName: string | null; reason: string; status: string;
  createdAt: string; expiresAt: string; finishedAt: string | null; deviceReply: string | null;
}
interface Detail {
  device: DeviceRow;
  latest: { id: string; checkedUtc: string; receivedAt: string; findings: { Findings: Finding[] } } | null;
  history: Array<{ id: string; checkedUtc: string; passCount: number; failCount: number; unknownCount: number }>;
  auditLines: Array<{ id: string; raw: string; at: string | null; source: string | null; op: string | null; result: string | null }>;
  commands: Command[];
  sessions: Array<{ id: string; reason: string; minutes: number; status: string; createdAt: string; expiresAt: string; activeAt: string | null; endedAt: string | null }>;
}

const THEMES = ["Firewalls", "Secure configuration", "User access control", "Malware protection", "Security update management"];
const OP_LABELS: Record<string, string> = { block: "Block a program", unblock: "Unblock a program", restorepoint: "Make a restore point", "support-disable": "Switch support access off" };
const STATUS_WORDS: Record<string, string> = { queued: "Waiting for the PC", delivered: "PC has it", done: "Done", refused: "The PC refused", expired: "Ran out of time" };
const STATUS_TONE: Record<string, StatusTone> = { queued: "none", delivered: "partial", done: "met", refused: "gap", expired: "none" };
const SESSION_WORDS: Record<string, string> = { waiting: "Waiting for the code to be used", active: "Support access is on", ended: "Ended", cancelled: "Cancelled", expired: "The code was never used" };
const SESSION_TONE: Record<string, StatusTone> = { waiting: "partial", active: "gap", ended: "met", cancelled: "none", expired: "none" };
const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", color: theme.color.muted, verticalAlign: "top" };
const errorText: React.CSSProperties = { ...text.hint, color: theme.color.dangerText, marginTop: 10 };

const statusTone = (s: Finding["Status"]): StatusTone => (s === "Pass" ? "met" : s === "Fail" ? "gap" : "none");

/** How the answer was established: the app's own check is strong; a person's word is not. */
function MethodTag({ method }: { method?: Finding["Method"] }) {
  if (method === "Enforced" || method === "Verified") return <StatusTag tone="met" title="The app checked this on the PC">{method === "Enforced" ? "Set by the app" : "Checked by the app"}</StatusTag>;
  if (method === "Guided") return <StatusTag tone="partial" title="The app walked a person through it">Guided</StatusTag>;
  if (method === "Declared") return <StatusTag tone="partial" title="A person said so; the app could not check">Declared by a person</StatusTag>;
  return <StatusTag tone="none">Not stated</StatusTag>;
}

export function DeviceDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const canManage = !!user?.permissions?.includes("integrations:admin");
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState("");
  const [op, setOp] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [support, setSupport] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get<Detail>(`/devices/${encodeURIComponent(id)}`)); setError(""); }
    catch (e) { setError(apiErrorMessage(e, "Could not load this device.")); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  const findings = data?.latest?.findings.Findings ?? [];
  const removed = data?.device.status === "revoked";

  return (
    <div className="page-pad">
      <Link to="/devices" style={{ ...text.hint, display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none", marginBottom: 10 }}><ArrowLeft size={13} /> All devices</Link>
      {error && <div style={{ ...errorText, marginTop: 0, marginBottom: 12 }}>{error}</div>}
      {!data ? (error ? null : <LoadingBlock />) : (
        <div style={{ display: "grid", gap: 20 }}>
          <div>
            <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}><Monitor size={20} /> {data.device.name}</h1>
            <p style={{ ...pageSubtitle, maxWidth: 780 }}>
              {data.device.os ?? "Windows PC"}. {removed ? "Removed from your organisation; its history is kept." : data.device.lastSeenAt ? `Last heard from ${fmtDateTime(data.device.lastSeenAt)}.` : "Not heard from yet."}
              {data.latest && ` Last checked ${fmtDateTime(data.latest.checkedUtc)}.`}
            </p>
          </div>

          <SheetCard label="Cyber Essentials findings" actions={findings.length > 0 ? (
            <button type="button" style={{ ...secondaryBtn, fontSize: 12 }} onClick={() => setShowAll((v) => !v)}>{showAll ? "Show only what needs attention" : "Show every check"}</button>
          ) : undefined}>
            {findings.length === 0 ? <EmptyState title="No findings yet" detail="The app sends its findings after its first check." /> : (
              <div style={{ display: "grid", gap: 18 }}>
                <div style={text.hint}>
                  A check marked <b>Checked by the app</b> was tested on the PC. One marked <b>Declared by a person</b> is only someone&apos;s word, and counts for less when you answer Cyber Essentials.
                </div>
                {[...THEMES, "Other"].map((theme_) => {
                  const rows = findings.filter((f) => (f.Tags?.CyberEssentials ?? "Other").toLowerCase() === theme_.toLowerCase() || (theme_ === "Other" && !THEMES.some((t) => t.toLowerCase() === (f.Tags?.CyberEssentials ?? "").toLowerCase())));
                  const shown = showAll ? rows : rows.filter((f) => f.Status !== "Pass" || f.Method === "Declared" || f.Method === "Guided");
                  if (rows.length === 0) return null;
                  return (
                    <div key={theme_}>
                      <div style={{ ...text.itemTitle, marginBottom: 6 }}>{theme_} <span style={{ ...text.hint, fontWeight: 400 }}>{rows.filter((f) => f.Status === "Pass").length} of {rows.length} pass</span></div>
                      {shown.length === 0 && <div style={text.hint}>Everything here passes and was checked by the app.</div>}
                      {shown.map((f) => (
                        <div key={f.Id} style={{ padding: "8px 0", borderTop: "1px solid var(--c-border)" }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <StatusTag tone={statusTone(f.Status)}>{f.Status === "Pass" ? "Pass" : f.Status === "Fail" ? "Fail" : "Not known"}</StatusTag>
                            <MethodTag method={f.Method} />
                            <span style={text.body}>{f.Title}</span>
                          </div>
                          {f.Detail && <div style={{ ...text.hint, marginTop: 4 }}>{f.Detail}</div>}
                          {f.Status === "Fail" && f.Fix && <div style={{ ...text.hint, marginTop: 4 }}><b>To fix:</b> {f.Fix}</div>}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </SheetCard>

          {canManage && !removed && (
            <SheetCard label="Ask this PC to do something">
              <p style={text.hint}>
                The platform signs the instruction; the PC checks the signature, then does it only if WingGuard&apos;s own rules allow. The PC picks it up the next time it connects. Support access can only be switched on by a person at the PC, never from here.
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                {Object.entries(OP_LABELS).map(([k, label]) => <button key={k} type="button" style={secondaryBtn} onClick={() => setOp(k)}>{label}</button>)}
                <button type="button" style={secondaryBtn} onClick={() => setSupport(true)}>Start a support session</button>
                <button type="button" style={dangerBtn} onClick={() => setRemoving(true)}>Remove this device</button>
              </div>
            </SheetCard>
          )}

          {data.sessions.length > 0 && (
            <SheetCard label="Support sessions">
              {data.sessions.map((s) => (
                <div key={s.id} style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "6px 0", borderTop: "1px solid var(--c-border)" }}>
                  <StatusTag tone={SESSION_TONE[s.status] ?? "none"}>{SESSION_WORDS[s.status] ?? s.status}</StatusTag>
                  <span style={text.body}>{s.reason}</span>
                  <span style={text.hint}>{s.minutes} minutes once used. Started {fmtDateTime(s.createdAt)}.</span>
                  {s.status === "waiting" && canManage && <button type="button" style={{ ...secondaryBtn, fontSize: 12 }} onClick={async () => { await api.post(`/devices/support-sessions/${s.id}/cancel`, {}); void load(); }}>Cancel</button>}
                </div>
              ))}
            </SheetCard>
          )}

          <SheetCard label="Instructions sent">
            {data.commands.length === 0 ? <EmptyState title="Nothing sent" detail="Instructions you send to this PC and the PC's answers appear here." /> : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                  <thead><tr style={tableHeadRow}><th style={th}>Instruction</th><th style={th}>Why</th><th style={th}>Sent</th><th style={th}>State</th><th style={th}>The PC said</th></tr></thead>
                  <tbody>
                    {data.commands.map((c) => (
                      <tr key={c.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                        <td style={{ ...td, color: theme.color.text }}>{OP_LABELS[c.op] ?? c.op}{c.programName && <div style={text.hint}>{c.programName}</div>}</td>
                        <td style={td}>{c.reason}</td>
                        <td style={td}>{fmtDateTime(c.createdAt)}</td>
                        <td style={td}><StatusTag tone={STATUS_TONE[c.status] ?? "none"}>{STATUS_WORDS[c.status] ?? c.status}</StatusTag></td>
                        <td style={td}>{c.deviceReply ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SheetCard>

          <SheetCard label="What the PC's helper has done">
            {data.auditLines.length === 0 ? <EmptyState title="Nothing recorded" detail="The helper's own log is sent here, so you can see every change it made." /> : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                  <thead><tr style={tableHeadRow}><th style={th}>When</th><th style={th}>Asked by</th><th style={th}>Action</th><th style={th}>Result</th></tr></thead>
                  <tbody>
                    {data.auditLines.slice(0, 100).map((l) => (
                      <tr key={l.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                        {l.at ? (
                          <>
                            <td style={td}>{fmtDateTime(l.at)}</td>
                            <td style={td}>{l.source === "local" ? "A person at the PC" : l.source}</td>
                            <td style={{ ...td, color: theme.color.text }}>{l.op}</td>
                            <td style={td}>{l.result}</td>
                          </>
                        ) : <td style={{ ...td, fontFamily: "monospace" }} colSpan={4}>{l.raw}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SheetCard>

          {data.history.length > 1 && (
            <SheetCard label="Earlier checks">
              <div style={{ display: "grid", gap: 4 }}>
                {data.history.slice(0, 15).map((h) => (
                  <div key={h.id} style={{ ...text.hint, display: "flex", gap: 12 }}>
                    <span style={{ minWidth: 150 }}>{fmtDateTime(h.checkedUtc)}</span>
                    <span>{h.passCount} pass, {h.failCount} fail, {h.unknownCount} not known</span>
                  </div>
                ))}
              </div>
            </SheetCard>
          )}
        </div>
      )}

      {op && data && <SendDialog device={data.device} op={op} onClose={() => setOp(null)} onSent={() => { setOp(null); void load(); }} />}
      {support && data && <SupportDialog device={data.device} onClose={() => { setSupport(false); void load(); }} />}
      {removing && data && (
        <ConfirmRemove name={data.device.name} onClose={() => setRemoving(false)} onDone={() => navigate("/devices")} id={data.device.id} />
      )}
    </div>
  );
}

function SendDialog({ device, op, onClose, onSent }: { device: DeviceRow; op: string; onClose: () => void; onSent: () => void }) {
  const needsProgram = op === "block" || op === "unblock";
  const [path, setPath] = useState("");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [hours, setHours] = useState(4);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true); setError("");
    try { await api.post(`/devices/${device.id}/commands`, { op, path: needsProgram ? path : undefined, name: needsProgram ? name : undefined, reason, lifetimeHours: hours }); onSent(); }
    catch (e) { setError(apiErrorMessage(e, "Could not send that.")); setBusy(false); }
  };
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`${OP_LABELS[op]} on ${device.name}`} onClose={onClose} />
      <div style={{ padding: 18, display: "grid", gap: 12 }}>
        {needsProgram && (
          <>
            <Field label="Full path of the program" help="For example C:\Program Files\Example\app.exe">
              <input style={input} value={path} onChange={(e) => setPath(e.target.value)} placeholder="C:\Program Files\..." />
            </Field>
            <Field label="A name people will recognise"><input style={input} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} /></Field>
          </>
        )}
        <Field label="Why are you doing this" help="Kept in the activity log with your name.">
          <textarea style={textarea} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
        </Field>
        <Field label="Keep the instruction open for">
          <select style={input} value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            {[1, 4, 12, 24].map((h) => <option key={h} value={h}>{h} hour{h === 1 ? "" : "s"}</option>)}
          </select>
        </Field>
        <p style={text.hint}>If the PC is off or not connected in that time, the instruction simply runs out. Nothing is sent without your confirmation here.</p>
        {error && <div style={errorText}>{error}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" style={btnPrimary} disabled={busy || reason.trim().length < 10 || (needsProgram && (!path.trim() || !name.trim()))} onClick={() => void send()}>
            {busy ? "Sending..." : "Confirm and send"}
          </button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function ConfirmRemove({ id, name, onClose, onDone }: { id: string; name: string; onClose: () => void; onDone: () => void }) {
  const [error, setError] = useState("");
  const go = async () => {
    try { await api.post(`/devices/${id}/remove`, {}); onDone(); }
    catch (e) { setError(apiErrorMessage(e, "Could not remove it.")); }
  };
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`Remove ${name}`} onClose={onClose} />
      <div style={{ padding: 18, display: "grid", gap: 12 }}>
        <p style={text.body}>The PC stops reporting and any instruction waiting for it is cancelled. Its history stays here. To connect it again, make a new code.</p>
        {error && <div style={errorText}>{error}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" style={dangerBtn} onClick={() => void go()}>Remove</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function SupportDialog({ device, onClose }: { device: DeviceRow; onClose: () => void }) {
  const [minutes, setMinutes] = useState(60);
  const [reason, setReason] = useState("");
  const [out, setOut] = useState<{ code: string; waitMinutes: number } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true); setError("");
    try { setOut(await api.post(`/devices/${device.id}/support-sessions`, { minutes, reason })); }
    catch (e) { setError(apiErrorMessage(e, "Could not start the session.")); }
    finally { setBusy(false); }
  };
  return (
    <ModalOverlay onClose={out ? undefined : onClose}>
      <ModalHeader title={`Support session for ${device.name}`} onClose={onClose} />
      <div style={{ padding: 18, display: "grid", gap: 12 }}>
        {!out ? (
          <>
            <p style={text.body}>
              You get a one-time code. Read it to the person at the PC over the phone or a chat you trust. They type it into WingGuard and approve Windows, and only then is a temporary support account made. They never see the account&apos;s password.
            </p>
            <Field label="How long should access last once it is used">
              <select style={input} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
                {[15, 30, 60, 120, 240, 480].map((m) => <option key={m} value={m}>{m < 60 ? `${m} minutes` : `${m / 60} hour${m === 60 ? "" : "s"}`}</option>)}
              </select>
            </Field>
            <Field label="Why is support needed" help="Kept in the activity log with your name.">
              <textarea style={textarea} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
            </Field>
            {error && <div style={errorText}>{error}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" style={btnPrimary} disabled={busy || reason.trim().length < 10} onClick={() => void start()}>{busy ? "Starting..." : "Get the code"}</button>
              <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <p style={text.body}>Read this code to the person at the PC. It is shown once and is not stored. Do not send it in writing.</p>
            <div style={{ ...text.ref, fontSize: 22, letterSpacing: 8, padding: "18px 16px", border: "1px dashed var(--c-borderStrong)", textAlign: "center" }}>{out.code}</div>
            <p style={text.hint}>The PC collects the request when it next connects, and it runs out after {out.waitMinutes} minutes if the code is not used. Three wrong tries cancel it. Once it works, the support account lasts {minutes} minutes.</p>
            <div><button type="button" style={btnPrimary} onClick={onClose}>Done</button></div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}
